import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle, SheetDescription } from "./ui/sheet";
import { ThemeToggle } from "./ThemeToggle";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { SearchCommand } from "./SearchCommand";
import { articleUiFor } from "../i18n/locales/articles-ui";

const MARKETS = [
  { to: "/gold", key: "gold" },
  { to: "/silver", key: "silver" },
  { to: "/bitcoin", key: "bitcoin" },
  { to: "/ethereum", key: "ethereum" },
  { to: "/oil/brent", key: "brent" },
  { to: "/oil/wti", key: "wti" },
  { to: "/forex/eur-usd", key: "eurusd" },
];

const TOOLS = [
  { to: "/tools/gold-calculator", label: "tools.goldCalc" },
  { to: "/tools/crypto-profit-calculator", label: "tools.cryptoCalc" },
  { to: "/tools/dca-calculator", label: "x.dcaCalc" },
  { to: "/tools/if-you-invested", label: "tools.investCalc" },
  { to: "/tools/percentage-change", label: "x.pctCalc" },
  { to: "/tools/investment-return", label: "x.returnCalc" },
  { to: "/tools/currency-converter", label: "tools.converter" },
];

function Brand() {
  return (
    <Link to="/" data-testid="brand-logo" className="flex items-center gap-2">
      <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground font-heading text-lg font-bold">M</span>
      <span className="font-heading text-xl font-semibold tracking-tight text-foreground">MARDATIQ</span>
    </Link>
  );
}

export function Header() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const articleUi = articleUiFor(i18n.language);

  const linkCls = ({ isActive }) =>
    `text-sm font-medium transition-colors hover:text-foreground ${isActive ? "text-foreground" : "text-muted-foreground"}`;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border glass-header">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-8">
          <Brand />
          <nav className="hidden items-center gap-6 lg:flex">
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-muted-foreground outline-none transition-colors hover:text-foreground" data-testid="nav-markets">
                {t("nav.markets")} <ChevronDown className="h-3.5 w-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[200px]">
                {MARKETS.map((m) => (
                  <DropdownMenuItem key={m.to} asChild>
                    <Link to={m.to} data-testid={`nav-market-${m.key}`}>{t(`nav.${m.key}`)}</Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem asChild>
                  <Link to="/markets" className="text-primary" data-testid="nav-all-markets">{t("common.seeAll")}</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-muted-foreground outline-none transition-colors hover:text-foreground" data-testid="nav-tools">
                {t("nav.tools")} <ChevronDown className="h-3.5 w-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[220px]">
                {TOOLS.map((m) => (
                  <DropdownMenuItem key={m.to} asChild>
                    <Link to={m.to} data-testid={`nav-tool-${m.to}`}>{t(m.label)}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <NavLink to="/compare" className={linkCls} data-testid="nav-compare">{t("nav.compare")}</NavLink>
            <NavLink to="/learn" className={linkCls} data-testid="nav-learn">{t("nav.learn")}</NavLink>
            <NavLink to="/articles" className={linkCls} data-testid="nav-articles">{articleUi.nav}</NavLink>
          </nav>
        </div>

        <div className="flex items-center gap-1">
          <SearchCommand />
          <LanguageSwitcher />
          <ThemeToggle />
          <div className="lg:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9" aria-label="Menu" data-testid="mobile-menu-trigger">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] overflow-y-auto">
                <SheetTitle className="sr-only">MARDATIQ</SheetTitle>
                <SheetDescription className="sr-only">Site navigation menu</SheetDescription>
                <div className="mt-4 flex flex-col gap-1">
                  <div className="px-2 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{t("nav.markets")}</div>
                  {MARKETS.map((m) => (
                    <SheetClose asChild key={m.to}>
                      <Link to={m.to} className="rounded-md px-2 py-2 text-sm hover:bg-accent" data-testid={`mobile-market-${m.key}`}>{t(`nav.${m.key}`)}</Link>
                    </SheetClose>
                  ))}
                  <div className="px-2 pb-1 pt-4 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{t("nav.tools")}</div>
                  {TOOLS.map((m) => (
                    <SheetClose asChild key={m.to}>
                      <Link to={m.to} className="rounded-md px-2 py-2 text-sm hover:bg-accent">{t(m.label)}</Link>
                    </SheetClose>
                  ))}
                  <div className="mt-3 border-t border-border pt-3">
                    <SheetClose asChild><Link to="/compare" className="block rounded-md px-2 py-2 text-sm hover:bg-accent">{t("nav.compare")}</Link></SheetClose>
                    <SheetClose asChild><Link to="/learn" className="block rounded-md px-2 py-2 text-sm hover:bg-accent">{t("nav.learn")}</Link></SheetClose>
                    <SheetClose asChild><Link to="/articles" className="block rounded-md px-2 py-2 text-sm hover:bg-accent">{articleUi.nav}</Link></SheetClose>
                    <SheetClose asChild><Link to="/methodology" className="block rounded-md px-2 py-2 text-sm hover:bg-accent">{t("x.methodologyTitle")}</Link></SheetClose>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
